import { Typography } from "@material-ui/core";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useFetchAllDailyOrders,
  useGetSectorsActive,
} from "../../api/orders/hooks";
import { BagIcon, ClockIcon } from "../../assets";
import { ClientRow } from "../../components";
import { Select } from "../../components/Select";
import { useEntity } from "../../hooks/useEntity";
import { getFirebaseRealTimeDatabase } from "../../services/firebaseRealTimeDatabase";
import { useStore } from "../../store";
import { KitchenOrders, Sector } from "../../types";
import { getClientNameForOrder } from "../../utils/getClientNameForOrder";
import { getSectorNameForOrder } from "../../utils/getSectorNameForOrder";
import {
  getScopedSectors,
  shouldShowSectorOthers,
} from "../../utils/sectorScope";
import useStyles from "./styles";

export const Request: React.FC = () => {
  const Styles = useStyles();

  const [entityId, setEntityId] = useState<string>("");
  const wakeLockRef = useRef<any>(null);

  const { data: _orders, refetch: reFetchOrders } = useFetchAllDailyOrders(
    entityId,
    true,
  );

  const { userManager } = useStore();
  const { data: sectors } = useGetSectorsActive(entityId, true);
  const scopedSectors = useMemo(
    () => getScopedSectors(sectors, userManager.segmentId),
    [sectors, userManager.segmentId],
  );
  const hasFixedSegment = !!userManager.segmentId;

  const [orderIsPending, setOrderIsPending] = useState<KitchenOrders>([]);
  const [orderFinalized, setOrderFinalized] = useState<KitchenOrders>([]);
  const [sectorsList, setSectorsList] = useState<any[]>([]);
  const [sectorId, setSectorId] = useState("all");
  const [sectorSelected, setSectorSelected] = useState("all");
  const { showSectors } = useEntity();

  const isRequestRoute = useCallback(
    () => location.pathname === "/requests",
    [location.pathname],
  );

  const formatList = () => {
    const listSectors = scopedSectors?.map((_sector: Sector) => ({
      label: _sector.name,
      value: _sector.id,
    }));

    const listSortedSectors: any[] = listSectors?.sort((a: any, b: any) =>
      a.label.localeCompare(b.label),);

    if (!hasFixedSegment) {
      listSortedSectors.unshift({ label: "Todos", value: "all" });
    }
    if (shouldShowSectorOthers(userManager.segmentId)) {
      listSortedSectors.push({ label: "Outros", value: "others" });
    }

    setSectorsList(listSortedSectors);
  };

  const filterOrdersBySector = (order: any) => {
    if (hasFixedSegment) {
      return order.items?.some(
        (item: any) => item.segment?.id === userManager.segmentId,
      );
    }

    if (sectorId === "all") return true;

    if (sectorId === "others") {
      return order.items?.some((item: any) => !item.segment);
    }

    return order.items?.some((item: any) => item.segment?.id === sectorId);
  };

  const loadOrders = async () => {
    if (isRequestRoute()) {
      if (_orders) {
        setOrderIsPending(
          [
            ..._orders
              .filter((order) => !order.isDelivered)
              .filter((order) => !order.isPending)
              .sort(
                (firstOrder, secondOrder) =>
                  new Date(secondOrder?.createdAt || 0).getTime() -
                  new Date(firstOrder?.createdAt || 0).getTime(),
              )
              .filter((order: any) => filterOrdersBySector(order)),
            ..._orders
              .filter((order) => !order.isDelivered)
              .filter((order) => order.isPending)
              .filter((order) => !order.isSchedule)
              .sort(
                (firstOrder, secondOrder) =>
                  new Date(firstOrder?.createdAt || 0).getTime() -
                  new Date(secondOrder?.createdAt || 0).getTime(),
              )
              .filter((order: any) => filterOrdersBySector(order)),
          ].slice(0, 20) as KitchenOrders,
        );
        setOrderFinalized(
          _orders
            .filter((order) => order.isDelivered)
            .filter((order: any) => filterOrdersBySector(order))
            .sort(
              (firstOrder, secondOrder) =>
                new Date(secondOrder?.deliveredAt || 0).getTime() -
                new Date(firstOrder?.deliveredAt || 0).getTime(),
            )
            .slice(0, 20) as KitchenOrders,
        );
      }
    }
  };

  const filterOrders = (id: string) => {
    setSectorSelected(id);
    setSectorId(id);
    loadOrders();
  };

  useEffect(() => {
    (async () => {
      await loadOrders();
    })();
  }, [_orders, sectorId]);

  useEffect(() => {
    if (userManager.entity) {
      setEntityId(userManager.entity);
    }
  }, [userManager]);

  useEffect(() => {
    getFirebaseRealTimeDatabase(userManager.entity, async () => {
      await reFetchOrders();
    });
  }, []);

  useEffect(() => {
    if (scopedSectors?.length) {
      formatList();
    }
  }, [scopedSectors, hasFixedSegment, userManager.segmentId]);

  useEffect(() => {
    if (hasFixedSegment && userManager.segmentId) {
      setSectorId(userManager.segmentId);
      setSectorSelected(userManager.segmentId);
    }
  }, [hasFixedSegment, userManager.segmentId]);

  useEffect(() => {
    const requestWakeLock = async () => {
      if ("wakeLock" in navigator) {
        try {
          const lock = await (navigator as any).wakeLock.request("screen");
          wakeLockRef.current = lock;
        } catch {
          return null;
        }
      }
      return null;
    };
    const simulateActivity = () => {
      const event = new MouseEvent("mousemove", {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: Math.random() * 10,
        clientY: Math.random() * 10,
      });
      document.dispatchEvent(event);
    };
    requestWakeLock();
    const intervalId = setInterval(simulateActivity, 60000);
    const handleVisibilityChange = () => {
      if (
        wakeLockRef.current !== null &&
        document.visibilityState === "visible"
      ) {
        requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      if (wakeLockRef.current !== null) {
        wakeLockRef.current.release?.().catch(() => undefined);
        wakeLockRef.current = null;
      }
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <div className={Styles.container}>
      {sectorsList?.length > 0 && (showSectors || hasFixedSegment) && (
        <>
          <div className={Styles.filterSector}>
            <Select
              label="Setor"
              options={sectorsList}
              value={sectorSelected}
              onChange={(val) => filterOrders(val)}
            />
          </div>
          <hr className={Styles.line} />
        </>
      )}
      <div className={Styles.warpper}>
        <div className={Styles.orderList}>
          <div className={Styles.titleView}>
            <ClockIcon />

            <Typography component="span" className={Styles.title}>
              Em Andamento
            </Typography>
          </div>

          <div className={Styles.list}>
            {orderIsPending.map((order) => (
              <ClientRow
                key={order.id}
                {...order.client}
                name={getClientNameForOrder(order)}
                sector={getSectorNameForOrder(order, showSectors)}
              />
            ))}
          </div>
        </div>

        <div className={Styles.orderList}>
          <div className={Styles.titleView}>
            <BagIcon />

            <Typography component="span" className={Styles.title}>
              Finalizados
            </Typography>
          </div>

          <div className={Styles.list}>
            {orderFinalized.map((order) => (
              <ClientRow
                key={order.id}
                {...order.client}
                name={getClientNameForOrder(order)}
                sector={getSectorNameForOrder(order, showSectors)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
